// src/composables/usePermissions.js
import { computed } from 'vue'
import { useRouter } from 'vue-router'

/**
 * Composable that provides route-based permissions + AR Module specific helpers
 */
export function usePermissions() {
  const router = useRouter()

  // Get stored menus (array of menu groups)
  const menus = computed(() => {
    try {
      const storedMenus = localStorage.getItem('menus')
      return storedMenus ? JSON.parse(storedMenus) : []
    } catch (error) {
      console.error('Error parsing menus from localStorage:', error)
      return []
    }
  })

  /**
   * Helper to normalize paths (remove trailing slashes, case insensitive)
   */
  const normalizePath = (path) => {
    if (!path) return ''
    return path.replace(/\/$/, '').toLowerCase()
  }

  /**
   * Finds the menu/submenu item that best matches current route path
   * @returns {Object|null} { menu, submenu, permissions } or null
   */
  const currentPermission = computed(() => {
    const currentRoute = router.currentRoute.value
    const currentPath = normalizePath(currentRoute.path)

    console.log('🔍 Permission Check:', {
      currentPath,
      currentFullPath: currentRoute.fullPath,
      menuCount: menus.value.length
    })

    // Debug: List all available paths
    const allPaths = menus.value.flatMap(group => [
      { type: 'main', path: group.tab_link, name: group.tab_name },
      ...(group.menu || []).map(sub => ({
        type: 'sub',
        path: sub.tab_link,
        name: sub.tab_name,
        parent: group.tab_name
      }))
    ]).filter(item => item.path && item.path !== '#')

    console.log('📋 Available menu paths:', allPaths)

    for (const group of menus.value) {
      // 1. Check main menu link
      if (group.tab_link && group.tab_link !== '#') {
        const normalizedGroupPath = normalizePath(group.tab_link)
        if (normalizedGroupPath === currentPath) {
          console.log('✅ Matched main menu:', group.tab_name)
          return {
            menu: group,
            submenu: null,
            permissions: group.permissions || {}
          }
        }
      }

      // 2. Check submenus
      if (group.menu && Array.isArray(group.menu)) {
        for (const sub of group.menu) {
          if (sub.tab_link) {
            const normalizedSubPath = normalizePath(sub.tab_link)
            if (normalizedSubPath === currentPath) {
              console.log('✅ Matched submenu:', sub.tab_name, 'in', group.tab_name)
              return {
                menu: group,
                submenu: sub,
                permissions: sub.permissions || {}
              }
            }
          }
        }
      }
    }

    console.warn('❌ No permission found for path:', currentPath)

    // Fallback: partial / segment-based matching
    return findFallbackPermission(currentRoute)
  })

  /**
   * Fallback permission finder using segment matching
   */
  const findFallbackPermission = (currentRoute) => {
    const currentPath = currentRoute.path
    const pathSegments = currentPath.split('/').filter(Boolean)
    console.log('🔄 Trying fallback matching for path segments:', pathSegments)

    let bestMatch = null
    let bestMatchScore = 0

    for (const group of menus.value) {
      // Main menu
      if (group.tab_link && group.tab_link !== '#') {
        const score = calculatePathMatchScore(pathSegments, group.tab_link)
        if (score > bestMatchScore) {
          bestMatchScore = score
          bestMatch = {
            menu: group,
            submenu: null,
            permissions: group.permissions || {}
          }
        }
      }

      // Submenus
      if (group.menu && Array.isArray(group.menu)) {
        for (const sub of group.menu) {
          if (sub.tab_link) {
            const score = calculatePathMatchScore(pathSegments, sub.tab_link)
            if (score > bestMatchScore) {
              bestMatchScore = score
              bestMatch = {
                menu: group,
                submenu: sub,
                permissions: sub.permissions || {}
              }
            }
          }
        }
      }
    }

    if (bestMatch) {
      console.log('🔄 Fallback match found with score:', bestMatchScore, bestMatch)
    }

    return bestMatch
  }

  /**
   * Score how well two paths match based on segments
   */
  const calculatePathMatchScore = (currentSegments, menuPath) => {
    if (!menuPath || menuPath === '#') return 0
    const menuSegments = menuPath.split('/').filter(Boolean)
    let score = 0

    for (let i = 0; i < Math.min(currentSegments.length, menuSegments.length); i++) {
      if (currentSegments[i] === menuSegments[i]) {
        score += 2 // exact match gets more weight
      } else if (menuSegments[i] && currentSegments[i]) {
        if (currentSegments[i].includes(menuSegments[i]) || menuSegments[i].includes(currentSegments[i])) {
          score += 1 // partial match
        } else {
          break
        }
      } else {
        break
      }
    }

    return score
  }

  // ────────────────────────────────────────────────
  //                General permission helpers
  // ────────────────────────────────────────────────

  const can = (action) => {
    if (!currentPermission.value) {
      console.log(`❌ No permission object found for action: ${action}`)
      return false
    }
    const hasPermission = !!currentPermission.value.permissions?.[action]
    console.log(`🔐 Permission check for "${action}":`, hasPermission)
    return hasPermission
  }

  const getAllPermissions = computed(() => {
    return currentPermission.value?.permissions || {}
  })

  const hasAnyPermission = (actions) => {
    if (!currentPermission.value) return false
    return actions.some(action => currentPermission.value.permissions?.[action])
  }

  const hasAllPermissions = (actions) => {
    if (!currentPermission.value) return false
    return actions.every(action => currentPermission.value.permissions?.[action])
  }

  // ────────────────────────────────────────────────
  //               AR Module specific helpers
  // ────────────────────────────────────────────────

  const arModuleMenu = computed(() => {
    return menus.value.find(tab =>
      tab.tab_link === '#ARModule' ||
      tab.tab_name?.toLowerCase().includes('ar module') ||
      tab.name?.toLowerCase().includes('ar module')
    )
  })

  const UsersModuleMenu = computed(() => {
    return menus.value.find(tab =>
      tab.tab_name === 'User' ||
      tab.tab_name.toLowerCase().includes('User') ||
      tab.name.toLowerCase().includes('User')
    )
  })

  const mdmModuleMenu = computed(() => {
    return menus.value.find(tab =>
      tab.tab_link === '/device/mdm-devices' ||
      tab.tab_name?.toLowerCase().includes('mdm') ||
      tab.name?.toLowerCase().includes('mdm')
    )
  })

  const findArSubmenuByPath = (targetPath) => {
    if (!arModuleMenu.value?.menu) return null
    return arModuleMenu.value.menu.find(item =>
      normalizePath(item.tab_link) === normalizePath(targetPath)
    )
  }

    const findUserSubmenuByPath = (targetPath) => {
    if (!UsersModuleMenu.value?.menu) return null
    return UsersModuleMenu.value.menu.find(item =>
      normalizePath(item.tab_link) === normalizePath(targetPath)
    )
  }

  const findSubmenuByPath = (targetPath) => {
    const normalizedTarget = normalizePath(targetPath)
    for (const item of menus.value) {
      if (normalizePath(item.tab_link) === normalizedTarget) {
        return item
      }
      if (item.menu && Array.isArray(item.menu)) {
        const found = item.menu.find(sub => normalizePath(sub.tab_link) === normalizedTarget)
        if (found) return found
      }
    }
    return null
  }

  // Read permissions for AR sub-modules
  const canReadCustomers     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.read)
  const canReadInvoices      = computed(() => !!findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions?.read)
  const canReadRefunds       = computed(() => !!findArSubmenuByPath('/ar/refund/refund-list')?.permissions?.read)
  const canReadCreditNotes   = computed(() => !!findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions?.read)
  const canReadInstantPay    = computed(() => !!findArSubmenuByPath('/ar/instantpay/list')?.permissions?.read)
  const canReadCollections   = computed(() => !!findArSubmenuByPath('/ar/collection/listing')?.permissions?.read)
  const canReadAgingReport   = computed(() => !!findArSubmenuByPath('/ar/reports/aging-report')?.permissions?.read)
  const canReadOrder         = computed(() => !!findSubmenuByPath('/orders/order-list')?.permissions?.read) 
  const canReadContracts     = computed(() => !!findSubmenuByPath('/contracts/contract-list')?.permissions?.read) 
  const canReadCustomersList = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.read) 
  const canReadInternalUser = computed(() => !!findUserSubmenuByPath('/user/user')?.permissions?.read)
  const canReadReferralCode = computed(() => !!findUserSubmenuByPath('/user/referralCode')?.permissions?.read)
  const canReadExternalUser = computed(() => !!findUserSubmenuByPath('/user/ext-users')?.permissions?.read)
  const canReadRole = computed(() => !!findUserSubmenuByPath('/user/role')?.permissions?.read)
  const canReadTnc = computed(() => !!findSubmenuByPath('/tnc')?.permissions?.read)

  // MDM specific
  const canReadMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.read)
  const canCreateMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.create)
  const canUpdateMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.update)
  const canDeleteMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.delete)
  const canApproveMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.approve)
  const canRequestMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.request)
  const canExportMdm = computed(() => !!findSubmenuByPath('/device/mdm-devices')?.permissions?.export)

  // Update permissions for AR sub-modules
  const canUpdateCustomers     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.update)
  const canUpdateInvoices      = computed(() => !!findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions?.update)
  const canUpdateRefunds       = computed(() => !!findArSubmenuByPath('/ar/refund/refund-list')?.permissions?.update)
  const canUpdateCreditNotes   = computed(() => !!findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions?.update)
  const canUpdateInstantPay    = computed(() => !!findArSubmenuByPath('/ar/instantpay/list')?.permissions?.update)
  const canUpdateCollections   = computed(() => !!findArSubmenuByPath('/ar/collection/listing')?.permissions?.update)
  const canUpdateAgingReport   = computed(() => !!findArSubmenuByPath('/ar/reports/aging-report')?.permissions?.update)
  const canUpdateOrder         = computed(() => !!findSubmenuByPath('/orders/order-list')?.permissions?.update) 
  const canUpdateContracts     = computed(() => !!findSubmenuByPath('/contracts/contract-list')?.permissions?.update) 
  const canUpdateCustomersList = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.update) 
  const canUpdateInternalUser = computed(() => !!findUserSubmenuByPath('/user/user')?.permissions?.update)
  const canUpdateReferralCode = computed(() => !!findUserSubmenuByPath('/user/referralCode')?.permissions?.update)
  const canUpdateExternalUser = computed(() => !!findUserSubmenuByPath('/user/ext-users')?.permissions?.update)
  const canUpdateRole = computed(() => !!findUserSubmenuByPath('/user/role')?.permissions?.update)
  const canUpdateTnc = computed(() => !!findSubmenuByPath('/tnc')?.permissions?.update)


  // Create permissions for AR sub-modules
  const canCreateCustomers     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.create)
  const canCreateInvoices      = computed(() => !!findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions?.create)
  const canCreateRefunds       = computed(() => !!findArSubmenuByPath('/ar/refund/refund-list')?.permissions?.create)
  const canCreateCreditNotes   = computed(() => !!findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions?.create)
  const canCreateInstantPay    = computed(() => !!findArSubmenuByPath('/ar/instantpay/list')?.permissions?.create)
  const canCreateCollections   = computed(() => !!findArSubmenuByPath('/ar/collection/listing')?.permissions?.create)
  const canCreateAgingReport   = computed(() => !!findArSubmenuByPath('/ar/reports/aging-report')?.permissions?.create)
  const canCreateOrder         = computed(() => !!findSubmenuByPath('/orders/order-list')?.permissions?.create) 
  const canCreateContracts     = computed(() => !!findSubmenuByPath('/contracts/contract-list')?.permissions?.create) 
  const canCreateCustomersList = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.create) 
  const canCreateInternalUser = computed(() => !!findUserSubmenuByPath('/user/user')?.permissions?.create)
  const canCreateReferralCode = computed(() => !!findUserSubmenuByPath('/user/referralCode')?.permissions?.create)
  const canCreateExternalUser = computed(() => !!findUserSubmenuByPath('/user/ext-users')?.permissions?.create)
  const canCreateRole = computed(() => !!findUserSubmenuByPath('/user/role')?.permissions?.create)
  const canCreateTnc = computed(() => !!findSubmenuByPath('/tnc')?.permissions?.create)

  // Export permissions for AR sub-modules
  const canExportCustomers     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.export)
  const canExportInvoices      = computed(() => !!findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions?.export)
  const canExportRefunds       = computed(() => !!findArSubmenuByPath('/ar/refund/refund-list')?.permissions?.export)
  const canExportCreditNotes   = computed(() => !!findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions?.export)
  const canExportInstantPay    = computed(() => !!findArSubmenuByPath('/ar/instantpay/list')?.permissions?.export)
  const canExportCollections   = computed(() => !!findArSubmenuByPath('/ar/collection/listing')?.permissions?.export)
  const canExportAgingReport   = computed(() => !!findArSubmenuByPath('/ar/reports/aging-report')?.permissions?.export)
  const canExportOrder         = computed(() => !!findSubmenuByPath('/orders/order-list')?.permissions?.export) 
  const canExportContracts     = computed(() => !!findSubmenuByPath('/contracts/contract-list')?.permissions?.export) 
  const canExportCustomersList = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.export) 
  const canExportInternalUser = computed(() => !!findUserSubmenuByPath('/user/user')?.permissions?.export)
  const canExportExternalUser = computed(() => !!findUserSubmenuByPath('/user/ext-users')?.permissions?.export)
  const canExportTnc = computed(() => !!findSubmenuByPath('/tnc')?.permissions?.export)

  // delete permissions for AR sub-modules
  const canDeleteCustomers     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.delete)
  const canDeleteInvoices      = computed(() => !!findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions?.delete)
  const canDeleteRefunds       = computed(() => !!findArSubmenuByPath('/ar/refund/refund-list')?.permissions?.delete)
  const canDeleteCreditNotes   = computed(() => !!findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions?.delete)
  const canDeleteInstantPay    = computed(() => !!findArSubmenuByPath('/ar/instantpay/list')?.permissions?.delete)
  const canDeleteCollections   = computed(() => !!findArSubmenuByPath('/ar/collection/listing')?.permissions?.delete)
  const canDeleteAgingReport   = computed(() => !!findArSubmenuByPath('/ar/reports/aging-report')?.permissions?.delete)
  const canDeleteOrder         = computed(() => !!findSubmenuByPath('/orders/order-list')?.permissions?.delete) 
  const canDeleteContracts     = computed(() => !!findSubmenuByPath('/contracts/contract-list')?.permissions?.delete) 
  const canDeleteCustomersList = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.delete) 
  const canDeleteInternalUser = computed(() => !!findUserSubmenuByPath('/user/user')?.permissions?.delete)
  const canDeleteExternalUser = computed(() => !!findUserSubmenuByPath('/user/ext-users')?.permissions?.delete)
  const canDeleteRole = computed(() => !!findUserSubmenuByPath('/user/role')?.permissions?.delete)
  const canDeleteTnc = computed(() => !!findSubmenuByPath('/tnc')?.permissions?.delete)


  // Approve permissions for Customer
  const canApproveCustomersAR     = computed(() => !!findArSubmenuByPath('/ar/customer/master/listing')?.permissions?.approve)
  const canApproveCustomers = computed(() => !!findSubmenuByPath('/customer/customer-list')?.permissions?.approve) 
  
  // Optional: grouped object (useful for debugging or v-if with multiple conditions)
  const arPermissions = computed(() => ({
    hasARModule: !!arModuleMenu.value,
    customers:   findArSubmenuByPath('/ar/customer/master/listing')?.permissions || {},
    invoices:    findArSubmenuByPath('/ar/invoices/invoice-listing')?.permissions || {},
    refunds:     findArSubmenuByPath('/ar/refund/refund-list')?.permissions || {},
    creditNotes: findArSubmenuByPath('/ar/credit_notes/credit-note-listing')?.permissions || {},
    instantPay:  findArSubmenuByPath('/ar/instantpay/list')?.permissions || {},
    collections: findArSubmenuByPath('/ar/collection/listing')?.permissions || {},
    agingReport: findArSubmenuByPath('/ar/reports/aging-report')?.permissions || {},
  }))

  // ────────────────────────────────────────────────
  //                     Exports
  // ────────────────────────────────────────────────
  return {
    // Core / general permissions
    currentPermission,
    getAllPermissions,
    canRead:    computed(() => can('read')),
    canCreate:  computed(() => can('create')),
    canUpdate:  computed(() => can('update')),
    canDelete:  computed(() => can('delete')),
    canApprove: computed(() => can('approve')),
    canRequest: computed(() => can('request')),
    canExport:  computed(() => can('export')),

    hasPermission:   can,
    hasAnyPermission,
    hasAllPermissions,

    // Utilities
    getMenuByPath: (path) => {
      const normalizedPath = normalizePath(path)
      for (const group of menus.value) {
        if (normalizePath(group.tab_link) === normalizedPath) {
          return { menu: group, submenu: null }
        }
        if (group.menu) {
          const sub = group.menu.find(s => normalizePath(s.tab_link) === normalizedPath)
          if (sub) return { menu: group, submenu: sub }
        }
      }
      return null
    },

    refreshPermissions: () => {
      // Force recompute (though usually not needed due to computed)
      menus.value = JSON.parse(localStorage.getItem('menus') || '[]')
    },

    // ── AR Module specific ───────────────────────────────────────
    arModuleMenu,
    canReadCustomers,
    canReadInvoices,
    canReadRefunds,
    canReadCreditNotes,
    canReadInstantPay,
    canReadCollections,
    canReadAgingReport,
    canCreateCustomers,
    canCreateInvoices,
    canCreateRefunds,
    canCreateCreditNotes,
    canCreateInstantPay,
    canCreateCollections,
    canCreateAgingReport,
    canUpdateCustomers,
    canUpdateInvoices,
    canUpdateRefunds,
    canUpdateCreditNotes,
    canUpdateInstantPay,
    canUpdateCollections,
    canUpdateAgingReport,
    canReadOrder,
    canReadContracts,
    canCreateOrder,
    canCreateContracts,
    canUpdateOrder,
    canUpdateContracts,
    canReadCustomersList,
    canDeleteTnc,
    canCreateTnc,
    canUpdateTnc,
    canExportTnc,
    canReadTnc,
    canUpdateCustomersList,
    canCreateCustomersList,
    canUpdateInternalUser,
    canUpdateExternalUser,
    canReadInternalUser,
    canReadReferralCode,
    canUpdateReferralCode,
    canCreateReferralCode,
    canReadExternalUser,   
    canCreateInternalUser,
    canCreateExternalUser,
    canExportCustomers,
    canExportInvoices,
    canExportRefunds,
    canExportCreditNotes,
    canExportInstantPay,
    canExportCollections,
    canDeleteCustomers,
    canDeleteInvoices,
    canDeleteRefunds,
    canDeleteCreditNotes,
    canDeleteInstantPay,
    canDeleteCollections,
    canDeleteAgingReport,
    canDeleteOrder,
    canDeleteContracts,
    canDeleteRole,
    canDeleteCustomersList,
    canDeleteInternalUser,
    canDeleteExternalUser,
    canExportAgingReport,
    canExportOrder,
    canExportContracts,
    canExportCustomersList,
    canExportInternalUser,
    canExportExternalUser,
    canCreateRole,
    canUpdateRole,
    canReadRole,
    canApproveCustomers,
    canApproveCustomersAR,
    
    // MDM Module specific
    mdmModuleMenu,
    canReadMdm,
    canCreateMdm,
    canUpdateMdm,
    canDeleteMdm,
    canApproveMdm,
    canRequestMdm,
    canExportMdm,

    // Optional convenience grouping
    arPermissions,
  }
}